import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, ChevronDown, Cpu, Briefcase, Wrench, ClipboardList, ClipboardCheck } from 'lucide-react';

const ADMIN_ROLE_CODES = new Set(['CEO', 'CTO', 'ADMIN', 'SUPER_ADMIN']);

// Map of tab names to routes
const tabRouteMap = {
  dashboard: { name: 'Dashboard', href: '/dashboard' },
  leads: { name: 'Leads', href: '/leads' },
  dealers: { name: 'Dealers', href: '/dealers' },
  customers: { name: 'Customers', href: '/customers' },
  inspections: { name: 'Inspections', href: '/inspections' },
  loans: { name: 'Loans', href: '/loans' },
  'ad-analytics': { name: 'Ads Management', href: '/ad-analytics' },
  hr: { name: 'HR Module', href: '/hr' },
  finance: { name: 'Finance', href: '/finance' },
  settings: { name: 'Settings', href: '/settings' },
  'system-monitor': { name: 'Monitor', href: '/system-monitor' },
  'obd-sdk': { name: 'OBD SDK', href: '/obd-sdk', icon: Cpu, adminOnly: true },
  mfcwl: { name: 'MFCWL', href: '/mfcwl', icon: Briefcase, adminOnly: true },
  'service-packages': { name: 'Service Packages', href: '/service-packages', icon: Wrench, adminOnly: true },
  'service-leads': { name: 'Service Leads', href: '/service-leads', icon: ClipboardList, adminOnly: false },
  'service-ops': { name: 'Service Ops', href: '/service-ops', icon: ClipboardCheck, adminOnly: false },
};

// Primary tabs shown directly in the navbar
// Order: Dashboard → Service Leads → Service Ops → Leads → Customers → Inspections → Loans → Ads Management → More
const primaryTabs = ['dashboard', 'service-leads', 'service-ops', 'leads', 'customers', 'inspections', 'loans', 'ad-analytics'];

// Tabs hidden under "More" dropdown
const moreTabs = ['dealers', 'mfcwl', 'service-packages', 'hr', 'finance', 'settings', 'system-monitor', 'obd-sdk'];

export const TopNavbar = () => {
  const { user, logout, visibleTabs } = useAuth();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Determine if current user is an admin (for admin-only tabs)
  const roleCode = (user?.role_code || user?.role || '').toUpperCase();
  const extraRoles = (user?.roles || []).map(r => (r.code || '').toUpperCase());
  const isAdmin = ADMIN_ROLE_CODES.has(roleCode) || extraRoles.some(r => ADMIN_ROLE_CODES.has(r));

  const primaryNav = primaryTabs
    .filter(tab => {
      const def = tabRouteMap[tab];
      if (!def) return false;
      if (def.adminOnly) return isAdmin;
      return visibleTabs.includes(tab);
    })
    .map(tab => tabRouteMap[tab])
    .filter(Boolean);

  const moreNav = moreTabs
    .filter(tab => {
      const def = tabRouteMap[tab];
      if (!def) return false;
      if (def.adminOnly) return isAdmin;          // admin-only items bypass visibleTabs
      return visibleTabs.includes(tab);
    })
    .map(tab => tabRouteMap[tab])
    .filter(Boolean);

  // Check if any "more" tab is currently active
  const moreActive = moreNav.some(item =>
    location.pathname === item.href || location.pathname.startsWith(item.href)
  );

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-blue-900 to-blue-800 shadow-lg" data-testid="top-navbar">
      <div className="flex items-center justify-between h-14 px-4">
        {/* Navigation Tabs */}
        <nav className="flex items-center h-full gap-1" data-testid="main-nav">
          {primaryNav.map((item) => {
            const isActive = location.pathname === item.href ||
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
            return (
              <NavLink key={item.name} to={item.href} data-testid={`nav-${item.name.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  isActive ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}>
                {item.name}
              </NavLink>
            );
          })}

          {/* More Dropdown */}
          {moreNav.length > 0 && (
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setMoreOpen(prev => !prev)}
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  moreActive ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                data-testid="nav-more-btn"
              >
                More
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
              </button>

              {moreOpen && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50" data-testid="more-dropdown">
                  {moreNav.map((item) => {
                    const isActive = location.pathname === item.href ||
                      location.pathname.startsWith(item.href);
                    return (
                      <NavLink key={item.name} to={item.href}
                        onClick={() => setMoreOpen(false)}
                        data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                        className={`block px-4 py-2.5 text-sm transition-colors ${
                          isActive ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                        }`}>
                        {item.name}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Right Side - User Info */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-sm">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="text-right hidden sm:block">
              <span className="text-sm text-white font-medium block">{user?.name}</span>
              <span className="text-xs text-white/60">{user?.role_name || user?.role} • {user?.country_name}</span>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-sm text-white/70 hover:text-white px-3 py-2 rounded-lg hover:bg-white/10 transition-all"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};
